import type { ReactElement, ReactNode } from 'react';
import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

/** Separación entre el trigger y el tooltip (8px). */
const GAP = 8;

/**
 * Estado global del tooltip visible. Solo puede haber uno abierto a la vez:
 * abrir uno cierra el anterior.
 */
let activeTooltipId: string | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getActiveTooltip() {
  return activeTooltipId;
}

function setActiveTooltip(id: string | null) {
  if (activeTooltipId === id) return;
  activeTooltipId = id;
  emit();
}

/** True en dispositivos con hover real (mouse). En táctil usamos tap. */
function canHover() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  if (window.matchMedia('(hover: none)').matches) return false;
  return true;
}

export function Tooltip({ content, children, position = 'right' }: TooltipProps) {
  const tooltipId = useId();
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const activeId = useSyncExternalStore(subscribe, getActiveTooltip, getActiveTooltip);
  const isOpen = activeId === tooltipId;

  const close = useCallback(() => {
    if (activeTooltipId === tooltipId) setActiveTooltip(null);
  }, [tooltipId]);

  // Posiciona el tooltip en coordenadas de viewport. Se renderiza en un portal,
  // así ningún contenedor con overflow:hidden lo recorta.
  const updatePosition = useCallback(() => {
    const host = wrapperRef.current;
    const tip = tooltipRef.current;
    if (!host || !tip) return;

    const rect = host.getBoundingClientRect();
    const tipWidth = tip.offsetWidth;
    const tipHeight = tip.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'bottom':
        top = rect.bottom + GAP;
        left = rect.left + rect.width / 2 - tipWidth / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tipHeight / 2;
        left = rect.left - GAP - tipWidth;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tipHeight / 2;
        left = rect.right + GAP;
        break;
      case 'top':
      default:
        top = rect.top - GAP - tipHeight;
        left = rect.left + rect.width / 2 - tipWidth / 2;
        break;
    }

    // Mantenerlo dentro del viewport, respetando los márgenes laterales de la
    // app: 16px (var(--spacing-4)) en mobile, 8px en desktop.
    const horizontalMargin = viewportWidth < 768 ? 16 : 8;
    const verticalMargin = 8;
    left = Math.min(
      Math.max(horizontalMargin, left),
      Math.max(horizontalMargin, viewportWidth - tipWidth - horizontalMargin),
    );
    top = Math.min(Math.max(verticalMargin, top), Math.max(verticalMargin, viewportHeight - tipHeight - verticalMargin));

    setCoords({ top, left });
  }, [position]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }

    updatePosition();

    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, updatePosition]);

  // Cerrar al tocar/click fuera y con Escape.
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setActiveTooltip(null);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveTooltip(null);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const mergedChild = useMemo(() => {
    const childProps = children.props as Record<string, unknown>;
    const onMouseEnter = childProps.onMouseEnter as ((e: unknown) => void) | undefined;
    const onMouseLeave = childProps.onMouseLeave as ((e: unknown) => void) | undefined;
    const onFocus = childProps.onFocus as ((e: unknown) => void) | undefined;
    const onBlur = childProps.onBlur as ((e: unknown) => void) | undefined;
    const onClick = childProps.onClick as ((e: unknown) => void) | undefined;

    return cloneElement(children as ReactElement<any>, {
      'aria-describedby': (isOpen ? tooltipId : undefined) as string | undefined,
      // Desktop: hover abre; al salir se cierra.
      onMouseEnter: (e: unknown) => {
        onMouseEnter?.(e);
        if (canHover()) setActiveTooltip(tooltipId);
      },
      onMouseLeave: (e: unknown) => {
        onMouseLeave?.(e);
        if (canHover()) close();
      },
      // Teclado: al enfocar abre; al salir del foco se cierra.
      onFocus: (e: unknown) => {
        onFocus?.(e);
        if (canHover()) setActiveTooltip(tooltipId);
      },
      onBlur: (e: unknown) => {
        onBlur?.(e);
        if (canHover()) close();
      },
      // Táctil: tap abre y vuelve a tocar para cerrar.
      onClick: (e: unknown) => {
        onClick?.(e);
        if (!canHover()) {
          setActiveTooltip(activeTooltipId === tooltipId ? null : tooltipId);
        }
      },
    });
  }, [children, isOpen, tooltipId, close]);

  const canRenderPortal = typeof document !== 'undefined';

  return (
    <span ref={wrapperRef} className={styles.wrapper}>
      {mergedChild}
      {isOpen &&
        canRenderPortal &&
        createPortal(
          <span
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className={styles.tooltip}
            style={{
              top: coords ? `${coords.top}px` : '-9999px',
              left: coords ? `${coords.left}px` : '-9999px',
              opacity: coords ? 1 : 0,
            }}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}

Tooltip.displayName = 'Tooltip';
